import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext"; // Импортируем контекст аутентификации
import { useNavigate } from "react-router-dom";
import TopPartners from "./analitica/TopPartners";
import OrdersOverview from "./analitica/OrdersOverview";
import { Container, Alert, Button, Form } from "react-bootstrap";
import TotalOrders from "./analitica/TotalOrdersDisplay";
import PartnersSalesChart from "./analitica/PartnerOrdersChart";
import PageHeader from "./PageHeader";

const DashboardAnalytics = () => {
  const { isAuthenticated, hasRole } = useAuth() || {};
  const navigate = useNavigate(); // Для перенаправления
  const [error, setError] = useState(null);

  const toISODate = (date) => date.toISOString().slice(0, 10);
  const defaultEnd = useMemo(() => new Date(), []);
  const defaultStart = useMemo(() => {
    const copy = new Date();
    copy.setDate(copy.getDate() - 29);
    return copy;
  }, []);
  const initialRange = {
    startDate: toISODate(defaultStart),
    endDate: toISODate(defaultEnd),
  };
  const [dateRange, setDateRange] = useState(initialRange);
  const [pendingRange, setPendingRange] = useState(initialRange);
  const [rangeError, setRangeError] = useState(null);

  // Проверяем роль пользователя
  useEffect(() => {
    if (!isAuthenticated || !hasRole(["admin", "manager"])) {
      setError("У вас нет доступа к этому разделу");
    } else {
      setError(null);
    }
  }, [hasRole, isAuthenticated, navigate]);

  const handleRangeField = (field) => (event) => {
    const value = event.target.value;
    setPendingRange((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyRange = (event) => {
    event.preventDefault();
    const { startDate, endDate } = pendingRange;
    if (!startDate || !endDate) {
      setRangeError("Укажите обе даты.");
      return;
    }
    if (startDate > endDate) {
      setRangeError("Дата начала не может быть позже даты окончания.");
      return;
    }
    setRangeError(null);
    setDateRange(pendingRange);
  };

  const resetToLast30Days = () => {
    const freshEnd = new Date();
    const freshStart = new Date();
    freshStart.setDate(freshEnd.getDate() - 29);
    const normalized = {
      startDate: toISODate(freshStart),
      endDate: toISODate(freshEnd),
    };
    setPendingRange(normalized);
    setDateRange(normalized);
    setRangeError(null);
  };

  if (error) {
    return (
      <Container className="page-loader text-center">
        <Alert variant="danger" className="shadow-sm">{error}</Alert>
        <Button className="mt-3 btn-rounded" onClick={() => navigate("/dashboard")}>
          Вернуться на Dashboard
        </Button>
      </Container>
    );
  }

  const { startDate, endDate } = dateRange;
  const rangeLabel = `${startDate} — ${endDate}`;

  return (
    <Container fluid="xl" className="compact py-3">
      <PageHeader
        title="Аналитика"
        subtitle="Срез показателей"
        right={<span className="chip chip-ghost">{rangeLabel}</span>}
      />

      <section className="section mb-3">
        <Form className="row g-3 align-items-end" onSubmit={handleApplyRange}>
          <div className="col-md-4">
            <Form.Label>Начало периода</Form.Label>
            <Form.Control
              type="date"
              value={pendingRange.startDate}
              onChange={handleRangeField("startDate")}
            />
          </div>
          <div className="col-md-4">
            <Form.Label>Конец периода</Form.Label>
            <Form.Control
              type="date"
              value={pendingRange.endDate}
              onChange={handleRangeField("endDate")}
            />
          </div>
          <div className="col-md-4 d-flex gap-2">
            <Button type="submit" className="btn-rounded flex-grow-1">
              Применить
            </Button>
            <Button
              type="button"
              variant="outline-secondary"
              className="btn-ghost btn-rounded"
              onClick={resetToLast30Days}
            >
              Последние 30 дней
            </Button>
          </div>
        </Form>
        {rangeError && (
          <Alert variant="warning" className="mt-3 mb-0">
            {rangeError}
          </Alert>
        )}
      </section>

      <div className="row g-3">
        <div className="col-lg-6">
          <section className="section h-100">
            <TopPartners startDate={startDate} endDate={endDate} />
          </section>
        </div>
        <div className="col-lg-6">
          <section className="section h-100">
            <PartnersSalesChart startDate={startDate} endDate={endDate} />
          </section>
        </div>
        <div className="col-12">
          <section className="section">
            <OrdersOverview startDate={startDate} endDate={endDate} />
          </section>
        </div>
      </div>

      <div className="text-center mt-3">
        <div className="chip chip-ghost d-inline-flex align-items-center gap-2">
          <span>Всего заказов</span>
          <TotalOrders startDate={startDate} endDate={endDate} />
        </div>
      </div>
    </Container>
  );
};

export default DashboardAnalytics;
